import { useContext, useState, useRef, useEffect } from 'react';
import DropDownSingle from '../../../../Components/DropDownSingle';
import SwitchInput from '../../../../Components/SwitchInput';
import InputConfig from '../../../../Components/InputConfig';
import { FvpConfigContext } from '../../../../Contexts/FvpConfigContext';
import { globalFloorsMapping } from '../../../../core/constants/gamUprConstants';
import { additionalParams } from '../../../../core/constants/vastTagParams';
import { getSiteConfig, syncDfpAdUnits } from '../../../../api/sites';
import { getDfpNetworkConfig } from '../../../../api/dfpNetworkConfig';
import DropDownMultiple from '../../../../Components/DropDownMultiple';
import {
	copyToClipboard,
	prepareUnsyncedAdUnitObj,
	validateVastTagDetails,
	getVastTagUrl,
	validateAdUnitSyncingData
} from '../../../../utilities/helpers';
import { initialAdUnitAndVastDetailsObj } from '../../../../utilities/initialConfigs';
import { getFvpVastTagHistory, updateFvpVastTagHistory } from '../../../../api/fvpVastHistory';
import { v4 as uuid } from 'uuid';
import { toast } from 'react-toastify';
import {
	CONFIG_SYNC_MODES,
	ACCESS_CONTROL_CONSTANTS,
	PLATFORMS,
	VAST_TAG_CONSTANTS
} from '../../../../core/constants';
import { ADUNITS } from '../../../UnitConfigGenerator/Components/NetworkData/AdUnits/constants';
import Loader from '../../../../Components/Loader';
import GeneralIconButton from '../../../../Components/GeneralIconButton';
import Paper from '@mui/material/Paper';
import { Box, Grid, IconButton, Typography, Button, Chip } from '@mui/material';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import useCommonStyles from '../../../../commonStyles';
import useStyles from '../NetworkData/styles';
import AdUnitAndFloorPriceHistory from './AdUnitAndFloorPriceHistory';
import PageHeading from '../../../../Components/PageHeading';

const { SYSTEMS } = ACCESS_CONTROL_CONSTANTS;

/**
 * @name AdUnitAndVastManager
 * @description This component is a manager for Syncing Ad Unit and Generating a vast tag Url Based on the Details.
 * @param {{keyData: 'networkData.adUnitAndVastManager'}} keyData - A keyData used to identify the configuration in the rawConfig object.
 */
const AdUnitAndVastManager = ({ keyData }) => {
	const classes = useStyles();
	const commonStyleClasses = useCommonStyles();
	const { fvpConfig } = useContext(FvpConfigContext);
	const [siteConfig, setSiteConfig] = useState(null);
	const [status, setStatus] = useState({ loading: true, adUnitSynced: false });
	const [adUnitAndVastDetailsObj, setAdUnitAndVastDetailsObj] = useState(
		structuredClone(initialAdUnitAndVastDetailsObj)
	);
	const [generatedVastTag, setGeneratedVastTag] = useState('');
	const [fvpVastTagHistory, setFvpVastTagHistory] = useState({});
	const topRef = useRef(null);

	const sectionIdsList = siteConfig?.ads.map((ad) => ad.videoSectionId) || [];
	// We can judge if the data is historic based on the fact that it contains `timestamp` property,
	// since it gets added on server-side when a vast tag is generated.
	const isDataHistoric = adUnitAndVastDetailsObj.timestamp ? true : false;

	useEffect(() => {
		(async function () {
			try {
				const { data } = await getSiteConfig(fvpConfig.siteId);
				const fvpVastHistoryResponse = await getFvpVastTagHistory(fvpConfig.siteId);
				setSiteConfig(data?.data?.site);
				setFvpVastTagHistory(
					fvpVastHistoryResponse?.data?.data?.fvpVastHistoryDoc?.historicalVastDetails
				);
				setStatus((prevStatus) => ({ ...prevStatus, loading: false }));
			} catch (error) {
				console.error('Error fetching site config:', error);
			}
		})();
		generateUniqueAdUnitCode();
	}, []);

	/**
	 * @name generateUniqueAdUnitCode
	 * @description Generates a unique ad unit code and updates the state.
	 */
	const generateUniqueAdUnitCode = () => {
		setAdUnitAndVastDetailsObj((prevState) => ({
			...prevState,
			adUnitName: `ap_${fvpConfig.siteId}_vast_${uuid().substring(0, 5)}`
		}));
		setStatus({ ...status, adUnitSynced: false });
	};

	/**
	 * @name resetDetails
	 * @description Resets the ad unit details to their initial values.
	 */
	const resetDetails = () => {
		setAdUnitAndVastDetailsObj({
			...structuredClone(initialAdUnitAndVastDetailsObj),
			adUnitName: `ap_${fvpConfig.siteId}_vast_${uuid().substring(0, 5)}`
		});

		setGeneratedVastTag('');
		setStatus({ ...status, adUnitSynced: false });
	};

	/**
	 * @name showHistoricalAdUnitDetails
	 * @description Displays historical ad unit details.
	 * @param {Object} historicalVastDetails - The historical vast details object.
	 */
	const showHistoricalAdUnitDetails = (historicalVastDetails) => {
		if (topRef.current) {
			topRef.current.scrollIntoView({ behavior: 'smooth' });
		}
		setAdUnitAndVastDetailsObj(historicalVastDetails);
		const vastTagUrl = getVastTagUrl(siteConfig, historicalVastDetails);
		setGeneratedVastTag(vastTagUrl);
		setStatus({ ...status, adUnitSynced: true });
	};

	/**
	 * @name getAdDetailsObj
	 * @description Returns the Ad Details Object.
	 * @param {Object} dfpNetworkConfigResponse - Dfp Network Config Response.
	 * @returns {Object} Object containing ad unit details required for dfp syncing.
	 */
	const getAdDetailsObj = (dfpNetworkConfigResponse) => {
		return {
			adUnit: {
				name: adUnitAndVastDetailsObj.adUnitName,
				gamCode: adUnitAndVastDetailsObj.gamCode
			},
			platform: PLATFORMS.DESKTOP,
			adUnitType: siteConfig.mcmId ? ADUNITS.KEY.DEFAULT_UNIT_KEY : ADUNITS.KEY.NO_MCM_UNIT_KEY,
			dfpNetworkConfig: dfpNetworkConfigResponse?.data?.data?.dfpNetworkConfig,
			videoSectionUnit: siteConfig.ads.find(
				(ad) => ad.videoSectionId === adUnitAndVastDetailsObj.sectionId
			)
		};
	};

	/**
	 * @name handleSyncAdUnit
	 * @description Function to handle the unsynced adUnits if there exists any by syncing them through INSTREAM_DFP_SYNC queue.
	 */
	const handleSyncAdUnit = async () => {
		try {
			validateAdUnitSyncingData(adUnitAndVastDetailsObj);
			const dfpNetworkConfigResponse = await getDfpNetworkConfig();
			const adDetailsObj = getAdDetailsObj(dfpNetworkConfigResponse);

			const singleUnsyncedAdUnitObj = prepareUnsyncedAdUnitObj(adDetailsObj);
			const unsyncedAdUnitsPayload = {
				siteId: siteConfig?.siteId,
				siteDomain: siteConfig?.siteDomain,
				shouldSyncSite: false,
				data: [singleUnsyncedAdUnitObj],
				mode: CONFIG_SYNC_MODES.DFP_SYNC,
				previousCouchBaseConfig: {}
			};

			await syncDfpAdUnits(unsyncedAdUnitsPayload);

			toast.success(
				'Ad Unit Syncing Triggered, Please wait for a few seconds to generate vast tag.',
				{ position: toast.POSITION.BOTTOM_RIGHT }
			);
			setStatus({ ...status, adUnitSynced: true });
		} catch (error) {
			toast.error(error?.message, { position: toast.POSITION.BOTTOM_RIGHT });
		}
	};

	/**
	 * @name handleVastTagGeneration
	 * @description Generates the VAST tag URL based on the ad unit details.
	 */
	const handleVastTagGeneration = async () => {
		try {
			validateVastTagDetails(adUnitAndVastDetailsObj);
			const vastTagUrl = getVastTagUrl(siteConfig, adUnitAndVastDetailsObj);
			setGeneratedVastTag(vastTagUrl);
			updateFvpVastTagHistory(fvpConfig.siteId, adUnitAndVastDetailsObj);

			toast.success('Vast Tag Generated Successfully', { position: toast.POSITION.BOTTOM_RIGHT });
		} catch (error) {
			toast.error(error?.message, { position: toast.POSITION.BOTTOM_RIGHT });
		}
	};

	/**
	 * @name appendAdditionalParam
	 * @description Appends Additional Param as Key-Value Pair to Unsynced Vast Details Object.
	 */
	const appendAdditionalParam = () => {
		const clonedObject = structuredClone(adUnitAndVastDetailsObj);
		// For Setting a Default Key which is not already in the unsynced vast object.
		const availableParam = additionalParams.find((param) => !clonedObject.hasOwnProperty(param));
		if (availableParam) {
			setAdUnitAndVastDetailsObj({ ...clonedObject, [availableParam]: '' });
		}
	};

	/**
	 * @name deleteAdditionalParam
	 * @description Deletes Additional Param from Unsynced Vast Details Object.
	 * @param {string} key - Key which needs to be deleted from the object.
	 */
	const deleteAdditionalParam = (key) => {
		const clonedObject = structuredClone(adUnitAndVastDetailsObj);
		delete clonedObject[key];
		setAdUnitAndVastDetailsObj(clonedObject);
	};

	/**
	 * @name updateAdditionalParamKey
	 * @description Updates Additional Param Key in Unsynced Vast Details Object.
	 * @param {string} currentKey - Current Key Name which needs to be updated.
	 * @param {string} updatedKey - The Updated Key Name.
	 */
	const updateAdditionalParamKey = (currentKey, updatedKey) => {
		const clonedObject = structuredClone(adUnitAndVastDetailsObj);

		if (clonedObject.hasOwnProperty(currentKey)) {
			clonedObject[updatedKey] = clonedObject[currentKey];
			delete clonedObject[currentKey];
		}

		setAdUnitAndVastDetailsObj(clonedObject);
	};

	/**
	 * @name updateAdditionalParamVal
	 * @description Updates Additional Param Value in Unsynced Vast Details Object.
	 * @param {string} key - Key whose value needs to be updated.
	 * @param {string} updatedVal - The Updated Value.
	 */
	const updateAdditionalParamVal = (key, updatedVal) => {
		const clonedObject = structuredClone(adUnitAndVastDetailsObj);
		clonedObject[key] = updatedVal;
		setAdUnitAndVastDetailsObj(clonedObject);
	};
	/**
	 * @name handleFloorPriceAddition
	 * @description - The function `handleFloorPriceAddition` updates the customParams key's value in the adUnitAndVastDetailsObj based on a
	 * given floor input value, it replaces prmsig value if it already exists else appends it to the customParam.
	 * @param {string} val - Floor Value
	 */
	const handleFloorPriceAddition = (val) => {
		const prmsigNewVal = globalFloorsMapping[parseFloat(val).toFixed(2)];
		if (!prmsigNewVal) {
			return toast.error("Invalid Floor Value", {
				position: toast.POSITION.BOTTOM_RIGHT
			})
		}
		const { PRMSIG } = VAST_TAG_CONSTANTS.KEYS;

		const params = new URLSearchParams(adUnitAndVastDetailsObj.customParams || '');
		params.set(PRMSIG, prmsigNewVal);

		setAdUnitAndVastDetailsObj({
			...adUnitAndVastDetailsObj,
			floorPrice: val,
			customParams: params.toString()
		});
	}

	if (status.loading) {
		return <Loader />;
	}

	return (
		<>
			<Paper className={commonStyleClasses.mainPagePaperMarginStyle}>
				<PageHeading
					headingName="Ad Units and Vast Manager"
					headingPath={['Dashboard', 'Fvp', 'Ad Units and Vast Manager']}
					description="Manage and Generate Vast Tags for FVP."
				/>
				<Typography
					style={{
						width: 'fit-content',
						margin: '2rem 5rem',
						color: '#000000',
						backgroundColor: '#ffeaea',
						borderRadius: '4px',
						padding: '1rem'
					}}
				>
					How it works: Populate the fields-Ad Unit, GAM Code and Section ID then sync ad unit to
					enable rest of the fields & generate VAST tag following that.
				</Typography>
				<Grid container spacing={1} style={{ width: '90%', marginLeft: '4rem', marginTop: '3rem' }}>
					<Grid
						item
						xs={6}
						ref={topRef}
						style={{ display: 'flex', justifyContent: 'space-between' }}
					>
						<InputConfig
							isObjectEditor={false}
							system={SYSTEMS.FVP_PLAYER}
							label="Ad Unit Name"
							keyData={keyData + '.adUnitName'}
							editable={!isDataHistoric}
							directValue={adUnitAndVastDetailsObj.adUnitName}
						/>
						<ChangeCircleIcon
							fontSize="large"
							style={{
								cursor: 'pointer',
								marginTop: '5rem',
								marginLeft: '2rem',
								pointerEvents: isDataHistoric ? 'none' : 'auto' // disabling generating new ad unit code, when historic data is populated
							}}
							color="primary"
							onClick={generateUniqueAdUnitCode}
						/>
					</Grid>

					<Grid item xs={6}>
						<DropDownSingle
							system={SYSTEMS.FVP_PLAYER}
							keyData={keyData + '.gamNetworkCode'}
							label="GAM Code"
							list={siteConfig?.connectedGamIds || []}
							formControlVariant="outlined"
							isObjectEditor={false}
							directValue={adUnitAndVastDetailsObj.gamCode}
							disabled={isDataHistoric}
							updateDirectValue={(val) =>
								setAdUnitAndVastDetailsObj({ ...adUnitAndVastDetailsObj, gamCode: val })
							}
							isMandatory={true}
						/>
					</Grid>
					<Grid item xs={12}>
						<DropDownSingle
							system={SYSTEMS.FVP_PLAYER}
							keyData={keyData + '.sectionId'}
							label="Section ID"
							list={sectionIdsList}
							formControlVariant="outlined"
							isObjectEditor={false}
							disabled={isDataHistoric}
							directValue={adUnitAndVastDetailsObj.sectionId}
							updateDirectValue={(val) =>
								setAdUnitAndVastDetailsObj({ ...adUnitAndVastDetailsObj, sectionId: val })
							}
							isMandatory={true}
						/>
					</Grid>
					<Grid item xs={6}>
						<DropDownSingle
							system={SYSTEMS.FVP_PLAYER}
							keyData={keyData + '.floorPrice'}
							label="Floor Price"
							list={Object.keys(globalFloorsMapping)}
							formControlVariant="outlined"
							disabled={!status.adUnitSynced}
							isObjectEditor={false}
							directValue={adUnitAndVastDetailsObj.floorPrice}
							updateDirectValue={(val) => handleFloorPriceAddition(val)}
							isMandatory={true}
						/>
					</Grid>
					<Grid item xs={6}>
						<InputConfig
							system={SYSTEMS.FVP_PLAYER}
							label="Custom Params"
							keyData={keyData + '.customParams'}
							isObjectEditor={false}
							editable={status.adUnitSynced}
							directValue={adUnitAndVastDetailsObj.customParams}
							updateDirectValue={(val) =>
								setAdUnitAndVastDetailsObj({ ...adUnitAndVastDetailsObj, customParams: val })
							}
							placeholder="key1=value1&key2=value2"
							isMandatory={true}
						/>
					</Grid>
					<Grid item xs={6} style={{ display: 'flex', alignItems: 'center' }}>
						<SwitchInput
							system={SYSTEMS.FVP_PLAYER}
							keyData={keyData + '.enableLiveTraffic'}
							label="Enable For Live Traffic"
							defaultChecked={false}
							disabled={!status.adUnitSynced}
							isObjectEditor={false}
							directValue={adUnitAndVastDetailsObj.enableForLiveTraffic}
							updateDirectValue={(val) =>
								setAdUnitAndVastDetailsObj({
									...adUnitAndVastDetailsObj,
									enableForLiveTraffic: val
								})
							}
						/>
					</Grid>
					<Grid item xs={6} style={{ display: 'flex', alignItems: 'center' }}>
						<SwitchInput
							system={SYSTEMS.FVP_PLAYER}
							keyData={keyData + '.enableChildNetwork'}
							label="Enable Child Network"
							disabled={!siteConfig?.mcmId || !status.adUnitSynced}
							defaultChecked={false}
							isObjectEditor={false}
							directValue={adUnitAndVastDetailsObj.enableChildNetwork}
							updateDirectValue={(val) =>
								setAdUnitAndVastDetailsObj({
									...adUnitAndVastDetailsObj,
									enableChildNetwork: val
								})
							}
						/>
					</Grid>
					<Grid item xs={6}>
						<DropDownSingle
							system={SYSTEMS.FVP_PLAYER}
							keyData={keyData + '.videoFormatType'}
							label="Video Format Type"
							list={VAST_TAG_CONSTANTS.VIDEO_FORMAT_TYPES}
							disabled={!status.adUnitSynced}
							formControlVariant="outlined"
							isObjectEditor={false}
							directValue={adUnitAndVastDetailsObj.videoFormatType}
							updateDirectValue={(val) =>
								setAdUnitAndVastDetailsObj({ ...adUnitAndVastDetailsObj, videoFormatType: val })
							}
							isMandatory={true}
						/>
					</Grid>
					<Grid item xs={6}>
						<DropDownMultiple
							system={SYSTEMS.FVP_PLAYER}
							keyData={keyData + '.sizes'}
							isObjectEditor={false}
							label="Sizes"
							disabled={!status.adUnitSynced}
							list={VAST_TAG_CONSTANTS.VAST_PLAYER_SZ}
							directValue={adUnitAndVastDetailsObj.sizes}
							updateDirectValue={(val) =>
								setAdUnitAndVastDetailsObj({ ...adUnitAndVastDetailsObj, sizes: val })
							}
							isMandatory={true}
						/>
					</Grid>
					<Grid item xs={6}>
						<InputConfig
							system={SYSTEMS.FVP_PLAYER}
							label="Description URL"
							keyData={keyData + '.descriptionUrl'}
							isObjectEditor={false}
							directValue={adUnitAndVastDetailsObj.descriptionUrl}
							editable={!isDataHistoric && status.adUnitSynced}
							updateDirectValue={(val) =>
								setAdUnitAndVastDetailsObj({ ...adUnitAndVastDetailsObj, descriptionUrl: val })
							}
							placeholder="https://example.com"
							isMandatory={true}
						/>
					</Grid>
					<Grid item xs={6}>
						<InputConfig
							system={SYSTEMS.FVP_PLAYER}
							label="Max Ad Duration (s)"
							keyData={keyData + '.maxAdDuration'}
							isObjectEditor={false}
							editable={status.adUnitSynced}
							directValue={adUnitAndVastDetailsObj.adDuration}
							updateDirectValue={(val) =>
								setAdUnitAndVastDetailsObj({ ...adUnitAndVastDetailsObj, adDuration: val })
							}
							inputType="number"
							isMandatory={true}
						/>
					</Grid>
					<Grid item xs={12}>
						<Box
							sx={{
								margin: '0 auto',
								textAlign: 'center',
								backgroundColor: 'red',
								width: 'fit-content'
							}}
						>
							<GeneralIconButton
								color="primary"
								fontSize="large"
								className={classes.icon}
								onClick={() => appendAdditionalParam()}
								name={'FVP_VAST_MANAGER_PARAM_ADD_ICON'}
								disabled={!status.adUnitSynced}
							>
								<AddCircleIcon />
							</GeneralIconButton>
						</Box>
					</Grid>

					{/* DropDown and Value Containers for Additional Vast Tag Params */}
					{Object.entries(adUnitAndVastDetailsObj).map(([key, value]) => {
						if (!Object.keys(initialAdUnitAndVastDetailsObj).includes(key) && key !== 'timestamp') {
							return (
								<Box
									style={{
										display: 'flex',
										width: '100%',
										position: 'relative',
										marginBottom: '2rem'
									}}
									key={key}
								>
									<Grid item xs={6}>
										<DropDownSingle
											system={SYSTEMS.FVP_PLAYER}
											isObjectEditor={false}
											directValue={key}
											updateDirectValue={(updatedKey) => updateAdditionalParamKey(key, updatedKey)}
											list={additionalParams}
											label="Param"
											formControlStyles={{ width: '65%', marginLeft: '2rem' }}
											labelStyles={{ width: '100%' }}
											formControlVariant="outlined"
											selectInputStyles={{ margin: '1rem 0' }}
											keyData={`${keyData}gamCode`}
										/>
									</Grid>
									<Grid item xs={6}>
										<InputConfig
											system={SYSTEMS.FVP_PLAYER}
											isObjectEditor={false}
											directValue={value}
											updateDirectValue={(updatedVal) => updateAdditionalParamVal(key, updatedVal)}
											label="Value"
											inputStyles={{ width: '60%', margin: '1rem 0' }}
											labelStyles={{ width: '60%' }}
											keyData={`${keyData}name`}
										/>
									</Grid>
									<GeneralIconButton
										system={SYSTEMS.FVP_PLAYER}
										name="FVP_VAST_MANAGER_PARAM_CLEAR_ICON"
										color="primary"
										fontSize="small"
										style={{ position: 'absolute', right: '0', top: '5rem' }}
										className={classes.delIcon}
										onClick={() => deleteAdditionalParam(key)}
									>
										<ClearIcon />
									</GeneralIconButton>
								</Box>
							);
						}
						return null;
					})}

					<Box
						style={{
							width: '100%',
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							marginBottom: '2rem'
						}}
					>
						<Button variant="outlined" style={{ marginLeft: '2rem' }} onClick={resetDetails}>
							Reset Details
						</Button>
						<Box>
							<Button
								variant="contained"
								style={{ marginRight: '2rem' }}
								onClick={handleSyncAdUnit}
								disabled={isDataHistoric || status.adUnitSynced}
							>
								Sync Ad Unit
							</Button>
							<Button
								variant="contained"
								disabled={!status.adUnitSynced}
								style={{ marginLeft: '2rem' }}
								onClick={handleVastTagGeneration}
							>
								{`${isDataHistoric ? 'Update Vast Tag' : 'Generate Vast Tag'}`}
							</Button>
						</Box>
					</Box>

					{generatedVastTag && (
						<>
							<Typography
								variant="h6"
								fontWeight={600}
								className={classes.heading}
								style={{ width: '100%' }}
							>
								Generated Vast Tag
							</Typography>
							<Box className={classes.output}>
								<IconButton
									style={{ position: 'absolute', top: '1rem', right: '1rem' }}
									onClick={() => copyToClipboard(generatedVastTag, 'Vast Tag Copied Successfully.')}
								>
									<ContentCopyIcon />
								</IconButton>
								<Typography>{generatedVastTag}</Typography>
							</Box>
						</>
					)}
				</Grid>
			</Paper>
			<AdUnitAndFloorPriceHistory
				fvpVastTagHistory={fvpVastTagHistory}
				showHistoricalAdUnitDetails={showHistoricalAdUnitDetails}
			/>
		</>
	);
};

export default AdUnitAndVastManager;
